import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Workflow, Search, Lock, Archive } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Skeleton } from './Skeleton';

export default function LinearTeamSelector({ sourceConnId, open, onOpenChange, onSelect }) {
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open && sourceConnId) {
      fetchTeams();
    } else {
      // Reset state when dialog closes
      setTeams([]);
      setFilteredTeams([]);
      setSearchQuery('');
    }
  }, [open, sourceConnId]);

  useEffect(() => {
    // Filter teams based on search query
    if (!searchQuery.trim()) {
      setFilteredTeams(teams);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredTeams(
        teams.filter(
          (team) =>
            team.name.toLowerCase().includes(query) ||
            team.key.toLowerCase().includes(query) ||
            (team.description && team.description.toLowerCase().includes(query))
        )
      );
    }
  }, [searchQuery, teams]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/integrations/linear/${sourceConnId}/teams`);
      const teamsList = response.data.teams || [];
      setTeams(teamsList);
      setFilteredTeams(teamsList);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch Linear teams';
      toast.error(errorMsg);
      console.error('Error fetching Linear teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTeam = (team) => {
    onSelect({
      team_id: team.id,
      team_key: team.key,
      team_name: team.name,
      filter_value: team.id,
      filter_label: `${team.key} - ${team.name}`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Workflow className="h-5 w-5" />
            Select Linear Team
          </DialogTitle>
          <DialogDescription>
            Choose a Linear team (board) to add as a source for this project
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Teams List */}
        <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[400px] space-y-2 py-4">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredTeams.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                {searchQuery ? 'No teams found matching your search' : 'No teams available'}
              </p>
            </Card>
          ) : (
            filteredTeams.map((team) => (
              <Card
                key={team.id}
                className="p-4 cursor-pointer hover:bg-accent transition-colors"
                onClick={() => handleSelectTeam(team)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{team.key}</span>
                      <span className="text-muted-foreground">-</span>
                      <span className="text-muted-foreground">{team.name}</span>
                      {team.private && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="h-3 w-3 mr-1" />
                          Private
                        </Badge>
                      )}
                      {team.archived && (
                        <Badge variant="outline" className="text-xs">
                          <Archive className="h-3 w-3 mr-1" />
                          Archived
                        </Badge>
                      )}
                    </div>
                    {team.description && (
                      <p className="text-sm text-muted-foreground">{team.description}</p>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

